/**
 * Copy ONE section of a form into another form: graftSection(sourceConfig, sectionId, targetConfig, opts).
 *
 * WHY THIS FILE EXISTS
 *   Onboarding keeps needing a section that already exists on one form (a consent paragraph, an
 *   insurance block, the airway questions) on a DIFFERENT form. The builder's Duplicate only works
 *   inside one form and Bulk Copy only moves whole forms. Two surfaces share this routine:
 *     - the form builder, same site: Add Section -> "Copy a section from another form"
 *       (mixins/form-builder/form-builder-event-handler.js); nothing is stored until Save;
 *     - Super Admin, cross-site: the teamio-admin COPY_FORM_SECTION action (teamio-admin/lib/section-copy.js).
 *
 * TWO COPIES, ONE ROUTINE
 *   teamio-admin/lib/section-copy.js is this file byte-for-byte except the final export line
 *   (there it is a CommonJS module.exports assignment); a sync test pins the two together. So this
 *   file stays import-free, plain ES2019, with exactly ONE export statement as its last line: edit
 *   both copies or neither. cypress/node-tests/forms-section-copy.test.js loads THIS source, strips
 *   the export and runs it, so the tests exercise the shipped code, not a mirror that can drift.
 *
 * CONTRACT
 *   graftSection(sourceConfig, sectionId, targetConfig, { uuid, now = Date.now, position = 'end' })
 *     -> { config, report }
 *   - uuid: REQUIRED function returning a fresh uuid string (HELPER.getUUIDv4 / uuid's v4).
 *   - position: 'end' | { afterSectionId }.
 *   - Pure: never mutates sourceConfig or targetConfig. `config` is a deep (JSON) copy of the target
 *     with the section added; the copied section, rows and controls are JSON copies of the source's.
 *   - Throws "Invalid config: ...", "Invalid options: ..." or "Section not found: ...".
 *
 * POLICIES (every change is reported; nothing is changed silently)
 *   1. Subset: the section, its rows[] (tab sections) and every control listed in section.controls[]
 *      and rows[r].controls[]. Ids missing from the source maps are skipped.
 *   2. Fresh ids "section-" / "row-" / "control-" + uuid(); every copied control gets
 *      createdAt = now() (the builder's "newly added" glow, views/builder/ControlView.vue).
 *   3. Names are kept unless the target already uses them: then "<name> Copy", "<name> Copy 2"...
 *      (the rule of generateUniqueControlName). A blank name stays blank.  -> { kind: 'renamed' }
 *   4. teamioODColumn is cleared when ANY target control already maps the same column (ViewForm
 *      joins two controls on one column; list columns are a hard one-per-form save gate,
 *      libraries/validate-od-unique-mapping.js).                           -> { kind: 'odMappingCleared' }
 *   5. visibilityRules (section and control, schema in libraries/rule-evaluator.js): a condition that
 *      names a copied control (by old name or old uniqueId) is rewritten to the copy's new key; a
 *      condition naming a field the TARGET form already has (same value key) is kept as-is and
 *      reported, so sections gated on a shared question (e.g. a yes/no asked on both forms) arrive
 *      still gated                                                        -> { kind: 'ruleLinked' }
 *      a condition naming anything else drops the WHOLE rule              -> { kind: 'ruleDropped' }
 *      Rules with no conditions, and conditions with a blank field, travel verbatim (they name no
 *      control, so they behave exactly as they did on the source form).
 *   6. sameAs validations (ruleType, case-insensitive, 'sameas'; additionalValue = the other field):
 *      rewritten the same way, kept when the target has that field (-> 'ruleLinked'), or that one
 *      validation is dropped.                                             -> { kind: 'ruleDropped' }
 *   7. Section / row defaults are backfilled (configs/section.js SECTION_DEFAULT_DATA incl.
 *      breakAfter: false; configs/row.js ROW_DEFAULT_DATA); every other key (headlineEs, labelEs,
 *      tabNameEs ...) travels verbatim. Headline collisions are allowed.
 *   8. sortOrder: the copy goes at the end, or directly after position.afterSectionId; then every
 *      section is renumbered 1..n in sorted order (doSortSection sorts by sortOrder).
 *   9. report[0] = { kind: 'copied', sectionId, sourceSectionId, rowIds, controlIds, controls, rows,
 *      sectionHeadline, sortOrder }, then the renamed / odMappingCleared / ruleDropped entries.
 *
 *   Report entry shapes:
 *     { kind: 'renamed', control, from, to }                       control = the copy's new uniqueId
 *     { kind: 'odMappingCleared', control, column }
 *     { kind: 'ruleDropped', rule: 'visibility' | 'sameAs', on, onType: 'section' | 'control', field }
 *     { kind: 'ruleLinked',  rule: 'visibility' | 'sameAs', on, onType: 'section' | 'control', field }
 */

/**
 * Mirrors SECTION_DEFAULT_DATA (configs/section.js). Pinned by the AGREEMENT test in
 * cypress/node-tests/forms-section-copy.test.js: add a key there, add it here.
 */
const SECTION_COPY_DEFAULTS = {
    uniqueId: '',

    headline: '',
    headlineAdditionalClass: '',

    subHeadline: '',
    subHeadlineAdditionalClass: '',

    isShowHeadline: true,

    sortOrder: 0,
    type: '',
    rows: [],
    controls: [],

    breakAfter: false,
};

/**
 * Mirrors ROW_DEFAULT_DATA (configs/row.js; ROW_TYPES.normal === 'normal'). Pinned by the same
 * AGREEMENT test. Not exported: only the section defaults are part of the public contract.
 */
const ROW_COPY_DEFAULTS = {
    uniqueId: '',
    additionalClass: '',
    type: 'normal',
    sortOrder: 0,
    controls: [],
    extendData: null,
};

function cloneJson(value) {
    return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isBlank(value) {
    return value === undefined || value === null || String(value).trim() === '';
}

function hasOwn(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
}

/**
 * The key a control's value is stored under, exactly as mixins/form-renderer/model.js builds it:
 * `name` unless empty, else `uniqueId`. Visibility rules and sameAs validations point at this key.
 */
function valueKeyOf(control) {
    return control.name && control.name !== '' ? control.name : control.uniqueId;
}

/**
 * generateUniqueControlName (form-builder-event-handler.js), over an explicit set of taken keys.
 * A back-filled "control-..." id used as a name becomes '' (the renderer then falls back to the
 * copy's fresh uniqueId) instead of "control-<uuid> Copy".
 */
function uniqueControlName(sourceName, taken) {
    if (isBlank(sourceName)) return '';
    if (String(sourceName).indexOf('control-') === 0) return '';

    const candidate = `${sourceName} Copy`;
    if (!taken.has(candidate)) return candidate;

    let i = 2;
    while (taken.has(`${sourceName} Copy ${i}`)) i++;
    return `${sourceName} Copy ${i}`;
}

function sortNumber(section) {
    const n = Number(section.sortOrder);
    return isFinite(n) ? n : 0;
}

/**
 * Copy one section (with its rows and controls) from sourceConfig into a copy of targetConfig.
 * @param {Object} sourceConfig - the source form's parsed ConfigData ({ sections, rows, controls })
 * @param {String} sectionId - key of the section in sourceConfig.sections
 * @param {Object} targetConfig - the target form's parsed ConfigData (never mutated)
 * @param {{ uuid: Function, now?: Function, position?: ('end'|{ afterSectionId: String }) }} opts
 * @returns {{ config: Object, report: Object[] }}
 */
function graftSection(sourceConfig, sectionId, targetConfig, opts) {
    const options = opts || {};
    if (typeof options.uuid !== 'function') {
        throw new Error('Invalid options: opts.uuid must be a function that returns a fresh uuid');
    }
    const now = typeof options.now === 'function' ? options.now : Date.now;
    const position = (options.position === undefined || options.position === null) ? 'end' : options.position;
    if (position !== 'end' && !(isPlainObject(position) && !isBlank(position.afterSectionId))) {
        throw new Error("Invalid options: position must be 'end' or { afterSectionId }");
    }

    if (!isPlainObject(sourceConfig) || !isPlainObject(sourceConfig.sections)) {
        throw new Error('Invalid config: the source form has no sections map');
    }
    if (!isPlainObject(targetConfig) || !isPlainObject(targetConfig.sections)) {
        throw new Error('Invalid config: the target form has no sections map');
    }

    const srcSection = hasOwn(sourceConfig.sections, sectionId) ? sourceConfig.sections[sectionId] : null;
    if (!isPlainObject(srcSection)) {
        throw new Error(`Section not found: ${sectionId}`);
    }
    const srcControls = isPlainObject(sourceConfig.controls) ? sourceConfig.controls : {};
    const srcRows = isPlainObject(sourceConfig.rows) ? sourceConfig.rows : {};

    // Deep copy FIRST: everything below writes to `config` and to JSON copies of source pieces,
    // never to the inputs (the builder passes its live, reactive formData as the target).
    const config = cloneJson(targetConfig);
    if (!isPlainObject(config.controls)) config.controls = {};
    if (!isPlainObject(config.rows)) config.rows = {};

    const afterSectionId = position === 'end' ? null : String(position.afterSectionId);
    if (afterSectionId !== null) {
        const anchorExists = Object.keys(config.sections).some((key) => {
            const s = config.sections[key];
            return isPlainObject(s) && (key === afterSectionId || s.uniqueId === afterSectionId);
        });
        if (!anchorExists) {
            throw new Error(`Section not found: ${afterSectionId} (position.afterSectionId is not in the target form)`);
        }
    }

    // What the target already uses, BEFORE anything is added: value keys (policy 3) and OD columns (policy 4).
    const takenKeys = new Set();
    const targetColumns = new Set();
    Object.keys(config.controls).forEach((id) => {
        const control = config.controls[id];
        if (!isPlainObject(control)) return;
        const key = valueKeyOf(control);
        if (key) takenKeys.add(String(key));
        if (!isBlank(control.teamioODColumn)) targetColumns.add(String(control.teamioODColumn).trim());
    });
    // Snapshot: the fields the target form ALREADY had. A rule naming one of these (and no copied
    // control) is linked to it rather than dropped (policies 5-6).
    const targetValueKeys = new Set(takenKeys);

    const changes = [];
    const stamp = now();
    const newSectionId = 'section-' + options.uuid();

    const newControlIdByOld = new Map(); // source map key -> copy's uniqueId (a control listed twice is copied once)
    const copiedControls = [];           // copies, in section order
    const newKeyByOldName = new Map();   // old non-blank name -> copy's value key
    const newIdByOldId = new Map();      // old uniqueId -> copy's uniqueId

    // Policies 1-4 for one control id; returns the copy's id, or null when the id is missing.
    const copyControl = (oldId) => {
        if (newControlIdByOld.has(oldId)) return newControlIdByOld.get(oldId);
        const src = hasOwn(srcControls, oldId) ? srcControls[oldId] : null;
        if (!isPlainObject(src)) return null;

        const control = cloneJson(src);
        const newId = 'control-' + options.uuid();
        control.uniqueId = newId;
        control.createdAt = stamp;

        if (!isBlank(control.name) && takenKeys.has(String(control.name))) {
            const to = uniqueControlName(control.name, takenKeys);
            changes.push({ kind: 'renamed', control: newId, from: control.name, to });
            control.name = to;
        }
        takenKeys.add(String(valueKeyOf(control)));

        if (!isBlank(control.teamioODColumn)) {
            const column = String(control.teamioODColumn).trim();
            if (targetColumns.has(column)) {
                changes.push({ kind: 'odMappingCleared', control: newId, column });
                control.teamioODColumn = '';
            }
        }

        if (!isBlank(src.name) && !newKeyByOldName.has(String(src.name))) {
            newKeyByOldName.set(String(src.name), valueKeyOf(control));
        }
        newIdByOldId.set(String(oldId), newId);
        if (!isBlank(src.uniqueId)) newIdByOldId.set(String(src.uniqueId), newId);

        newControlIdByOld.set(oldId, newId);
        copiedControls.push(control);
        return newId;
    };

    const sectionControlIds = (Array.isArray(srcSection.controls) ? srcSection.controls : [])
        .map((oldId) => copyControl(oldId))
        .filter(Boolean);

    const newRowIdByOld = new Map();
    const copiedRows = [];
    const sectionRowIds = (Array.isArray(srcSection.rows) ? srcSection.rows : [])
        .map((oldRowId) => {
            if (newRowIdByOld.has(oldRowId)) return newRowIdByOld.get(oldRowId);
            const srcRow = hasOwn(srcRows, oldRowId) ? srcRows[oldRowId] : null;
            if (!isPlainObject(srcRow)) return null;

            const row = Object.assign(cloneJson(ROW_COPY_DEFAULTS), cloneJson(srcRow));
            row.uniqueId = 'row-' + options.uuid();
            row.controls = (Array.isArray(srcRow.controls) ? srcRow.controls : [])
                .map((oldId) => copyControl(oldId))
                .filter(Boolean);

            newRowIdByOld.set(oldRowId, row.uniqueId);
            copiedRows.push(row);
            return row.uniqueId;
        })
        .filter(Boolean);

    const section = Object.assign(cloneJson(SECTION_COPY_DEFAULTS), cloneJson(srcSection));
    section.uniqueId = newSectionId;
    section.controls = sectionControlIds;
    section.rows = sectionRowIds;

    // Policies 5-6: references are resolved only against the copied controls.
    const resolveField = (field) => {
        const key = String(field);
        if (newKeyByOldName.has(key)) return newKeyByOldName.get(key);
        if (newIdByOldId.has(key)) return newIdByOldId.get(key);
        return null;
    };

    const rewriteVisibilityRules = (holder, onId, onType) => {
        if (!Array.isArray(holder.visibilityRules)) return;
        holder.visibilityRules = holder.visibilityRules.filter((rule) => {
            const conditions = isPlainObject(rule) && Array.isArray(rule.conditions) ? rule.conditions : [];
            let dangling = null;
            const linked = [];
            conditions.forEach((condition) => {
                if (dangling !== null || !isPlainObject(condition) || isBlank(condition.field)) return;
                if (resolveField(condition.field) !== null) return;
                const field = String(condition.field);
                if (targetValueKeys.has(field)) {
                    if (linked.indexOf(field) === -1) linked.push(field);
                    return;
                }
                dangling = condition.field;
            });
            if (dangling !== null) {
                changes.push({ kind: 'ruleDropped', rule: 'visibility', on: onId, onType, field: dangling });
                return false;
            }
            conditions.forEach((condition) => {
                if (!isPlainObject(condition) || isBlank(condition.field)) return;
                const next = resolveField(condition.field);
                if (next !== null) condition.field = next; // linked fields keep their name verbatim
            });
            linked.forEach((field) => {
                changes.push({ kind: 'ruleLinked', rule: 'visibility', on: onId, onType, field });
            });
            return true;
        });
    };

    const rewriteSameAs = (control) => {
        if (!Array.isArray(control.validations)) return;
        control.validations = control.validations.filter((validation) => {
            if (!isPlainObject(validation) || String(validation.ruleType || '').toLowerCase() !== 'sameas') return true;
            if (isBlank(validation.additionalValue)) return true;
            const next = resolveField(validation.additionalValue);
            if (next === null) {
                if (targetValueKeys.has(String(validation.additionalValue))) {
                    changes.push({ kind: 'ruleLinked', rule: 'sameAs', on: control.uniqueId, onType: 'control', field: validation.additionalValue });
                    return true;
                }
                changes.push({ kind: 'ruleDropped', rule: 'sameAs', on: control.uniqueId, onType: 'control', field: validation.additionalValue });
                return false;
            }
            validation.additionalValue = next;
            return true;
        });
    };

    rewriteVisibilityRules(section, newSectionId, 'section');
    copiedControls.forEach((control) => {
        rewriteVisibilityRules(control, control.uniqueId, 'control');
        rewriteSameAs(control);
    });

    copiedControls.forEach((control) => { config.controls[control.uniqueId] = control; });
    copiedRows.forEach((row) => { config.rows[row.uniqueId] = row; });

    // Policy 8: place by position in the sorted order (ties keep their map order), then renumber 1..n.
    const ordered = Object.keys(config.sections)
        .filter((key) => isPlainObject(config.sections[key]))
        .map((key) => ({ key, section: config.sections[key] }))
        .sort((a, b) => sortNumber(a.section) - sortNumber(b.section));
    let insertAt = ordered.length;
    if (afterSectionId !== null) {
        const anchor = ordered.findIndex((entry) => entry.key === afterSectionId || entry.section.uniqueId === afterSectionId);
        insertAt = anchor + 1;
    }
    ordered.splice(insertAt, 0, { key: newSectionId, section });
    ordered.forEach((entry, i) => { entry.section.sortOrder = i + 1; });
    config.sections[newSectionId] = section;

    const controlIds = copiedControls.map((control) => control.uniqueId);
    const rowIds = copiedRows.map((row) => row.uniqueId);
    const copied = {
        kind: 'copied',
        sectionId: newSectionId,
        sourceSectionId: sectionId,
        rowIds,
        controlIds,
        controls: controlIds.length,
        rows: rowIds.length,
        sectionHeadline: section.headline,
        sortOrder: section.sortOrder,
    };

    return { config, report: [copied].concat(changes) };
}

export { graftSection, SECTION_COPY_DEFAULTS }
